import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDesktopNav } from "./navigation/DesktopNavContext";
import { supabase } from "../supabase";
import { loadScopedActiveProjects } from "../helpers/operationalQueryAdapters";
import { getActiveProjects } from "../helpers/projectVisibility";
import { blockDemoSave, shouldSkipSupabase } from "../helpers/guestMode";
import { getLocalDirectReportIds } from "../helpers/offlineBoot";
import { usePermissions } from "../hooks/usePermissions";
import { CAPABILITY } from "../helpers/permissions";
import { hardwareBackLog } from "../navigation/hardwareBackDebug";
import {
  buildN8nWebhookUrl,
  isN8nWebhookConfigured,
  WEBHOOK_PATHS,
} from "../helpers/webhookConfig";
import { SignOutButton } from "./SignOutButton";
import { CPDropdown } from "./CPDropdown";
import { CPInput } from "./CPInput";
import { CPTextarea } from "./CPTextarea";
import { useIsDesktopViewport } from "../hooks/useIsDesktopViewport";
import { CommandCenterDesktop } from "./ops/CommandCenterDesktop";

function assertIntakeWebhookAllowed(currentUser) {
  if (!isN8nWebhookConfigured()) {
    return "Voice intake is not configured for this environment.";
  }
  if (shouldSkipSupabase(currentUser)) {
    return "Sign in to use voice intake.";
  }
  return null;
}

async function postIntakeWebhook(currentUser, body) {
  const blocked = assertIntakeWebhookAllowed(currentUser);
  if (blocked) {
    throw new Error(blocked);
  }
  const url = buildN8nWebhookUrl(WEBHOOK_PATHS.INTAKE);
  if (!url) {
    throw new Error("Voice intake is not configured.");
  }
  return fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

async function postFinalizeWebhook(currentUser, body) {
  const blocked = assertIntakeWebhookAllowed(currentUser);
  if (blocked) {
    throw new Error(blocked);
  }
  const url = buildN8nWebhookUrl(WEBHOOK_PATHS.FINALIZE_TIMELOG);
  if (!url) {
    throw new Error("Session finalization is not configured.");
  }
  return fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

// ── Time tracking states ───────────────────────────────────
const TS = {
  IDLE: "idle",
  ACTIVE: "active",
  BREAK: "break",
  STARTING: "starting",
};

const ACTIVE_SESSION_KEY = "crewpilot_active_session";
const PENDING_FINALIZATION_KEY = "crewpilot_pending_finalization";

/** Beta UX: hide preview / not-yet-operational hub cards (presentation only). */
const COCKPIT_HUB_HIDE_BETA_CARDS = true;

// ── Utility: format seconds → HH:MM:SS ────────────────────
function formatTime(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

// ── Utility: format Date → "9:41 AM" ──────────────────────
function formatClock(date) {
  if (!date) return "--:-- --";
  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

// ── Default seed projects (replaces hardcoded JOB_OPTIONS) ─
const DEFAULT_PROJECTS = [
  { id: "none", name: "None Selected", number: "", client: "", notes: "" },
  {
    id: "seed-1",
    name: "Site A — Foundation Work",
    number: "101",
    client: "",
    notes: "",
  },
  {
    id: "seed-2",
    name: "Site B — Electrical Fit-Out",
    number: "102",
    client: "",
    notes: "",
  },
  {
    id: "seed-3",
    name: "Site C — Roofing",
    number: "103",
    client: "",
    notes: "",
  },
  {
    id: "seed-4",
    name: "Office — Admin & Planning",
    number: "104",
    client: "",
    notes: "",
  },
  {
    id: "seed-5",
    name: "Warehouse — Inventory",
    number: "105",
    client: "",
    notes: "",
  },
];

const CATEGORY_OPTIONS = [
  "General Labour",
  "Skilled Trade",
  "Site Supervision",
  "Equipment Operation",
  "Admin / Planning",
  "Travel / Transport",
  "Other",
];

// ── New Project Modal ──────────────────────────────────────
function NewProjectModal({ onSave, onClose }) {
  const [form, setForm] = useState({
    name: "",
    number: "",
    client: "",
    notes: "",
  });

  const handleChange = (field) => (value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSave = () => {
    const newProject = {
      id: `proj-${Date.now()}`,
      name: form.name.trim() || "Unnamed Project",
      number: form.number.trim(),
      client: form.client.trim(),
      notes: form.notes.trim(),
    };
    onSave(newProject);
  };

  // Close on overlay click
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="np-overlay" onClick={handleOverlayClick}>
      <div className="np-card">
        {/* Header */}
        <div className="np-header">
          <div className="np-header-left">
            <div className="np-header-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 7h18" />
                <path d="M5 7V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v2" />
                <rect x="3" y="7" width="18" height="13" rx="2" />
                <line x1="12" y1="11" x2="12" y2="17" />
                <line x1="9" y1="14" x2="15" y2="14" />
              </svg>
            </div>
            <div>
              <div className="np-title">New Project</div>
              <div className="np-subtitle">All fields optional</div>
            </div>
          </div>
          <button className="np-close-btn" onClick={onClose} aria-label="Close">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Fields — canonical CP* ordinary controls */}
        <div className="np-fields">
          <CPInput
            className="cp-input-group--compact"
            label="Project Name"
            placeholder="e.g. Kitchen Renovation"
            value={form.name}
            onChange={handleChange("name")}
            autoFocus
          />

          <CPInput
            className="cp-input-group--compact"
            label="Project Number"
            placeholder="e.g. 204"
            value={form.number}
            onChange={handleChange("number")}
          />

          <CPInput
            className="cp-input-group--compact"
            label="Client Name"
            placeholder="e.g. Bob's Lawn Care"
            value={form.client}
            onChange={handleChange("client")}
          />

          <CPTextarea
            className="cp-input-group--compact"
            label="Notes"
            placeholder="Any additional project notes…"
            value={form.notes}
            onChange={handleChange("notes")}
            rows={3}
          />
        </div>

        {/* Actions */}
        <div className="np-actions">
          <button className="gold-action-btn np-save-btn" onClick={handleSave}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ width: 15, height: 15 }}
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Save Project
          </button>
          <button
            className="outline-action-btn np-cancel-btn"
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export function CockpitHubScreen({
  onBack,
  session,
  currentUser,
  onOpenRequest,
  onOpenManualEntry,
  onOpenCreateClient,
  onOpenCreateProject,
  onOpenTimeEntry,
  onOpenTimeTrackingV2,
  onSignOut,
}) {
  const desktopNav = useDesktopNav();
  const isDesktop = useIsDesktopViewport();
  const { can } = usePermissions();
  const canCreateClient = can(CAPABILITY.CLIENT_CREATE);
  const [trackingState, setTrackingState] = useState(TS.IDLE);
  const [selectedJobId, setSelectedJobId] = useState("none");
  const [projects, setProjects] = useState([
    {
      id: "none",
      name: "None Selected",
      number: "",
      client: "",
      notes: "",
    },
  ]);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  useEffect(() => {
    loadProjects();
  }, []);

  async function loadProjects() {
    if (shouldSkipSupabase(currentUser)) {
      setProjects([
        {
          id: "none",
          name: "None Selected",
          number: "",
          client: "",
          notes: "",
        },
        {
          id: "guest-1",
          name: "Highway 17 Soil Investigation",
          number: "",
          client: "",
          notes: "",
        },
        {
          id: "guest-2",
          name: "Municipal Water Study",
          number: "",
          client: "",
          notes: "",
        },
      ]);
      return;
    }

    const directReportIds = getLocalDirectReportIds();

    try {
      const data = await loadScopedActiveProjects(
        supabase,
        currentUser,
        { directReportIds },
      );

      setProjects([
        {
          id: "none",
          name: "None Selected",
          number: "",
          client: "",
          notes: "",
        },
        ...(getActiveProjects(data || []).map((p) => ({
          id: p.project_id,
          name: p.project_name,
          number: "",
          client: p.client_id || "",
          notes: p.notes || "",
        }))),
      ]);
    } catch (error) {
      console.error("PROJECT LOAD ERROR:", error);
    }
  }

  const [startTime, setStartTime] = useState(null);
  const [, setBreakStartTime] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [breakElapsed, setBreakElapsed] = useState(0);
  const [totalBreak, setTotalBreak] = useState(0);
  const [showModal, setShowModal] = useState(false);

  const [workCompleted, setWorkCompleted] = useState("");
  const [workCategory, setWorkCategory] = useState(CATEGORY_OPTIONS[0]);
  const [notes, setNotes] = useState("");

  const workIntervalRef = useRef(null);
  const breakIntervalRef = useRef(null);
  const timeTrackingRef = useRef(null);
  const touchStartX = useRef(null);

  useEffect(() => {
    hardwareBackLog("CockpitHub mounted — App screen should be crewpilot", {
      historyTrapPresent: Boolean(
        window.history?.state?.crewpilotBackTrap,
      ),
      historyStateScreen: window.history?.state?.screen ?? null,
    });
    return () => {
      hardwareBackLog("CockpitHub unmounted");
    };
  }, []);

  // ── Derived: active project object ─────────────────────
  const selectedProject =
    projects.find((p) => p.id === selectedJobId) || projects[0];
  const selectedJobLabel =
    selectedProject?.id === "none"
      ? "None Selected"
      : selectedProject?.name || "None Selected";

  // ── Tick helpers ────────────────────────────────────────
  const startWorkTick = useCallback(() => {
    clearInterval(workIntervalRef.current);
    workIntervalRef.current = setInterval(() => {
      setElapsed((prev) => prev + 1);
    }, 1000);
  }, []);

  const stopWorkTick = useCallback(() => {
    clearInterval(workIntervalRef.current);
    workIntervalRef.current = null;
  }, []);

  const startBreakTick = useCallback(() => {
    clearInterval(breakIntervalRef.current);
    breakIntervalRef.current = setInterval(() => {
      setBreakElapsed((prev) => prev + 1);
    }, 1000);
  }, []);

  const stopBreakTick = useCallback(() => {
    clearInterval(breakIntervalRef.current);
    breakIntervalRef.current = null;
  }, []);

  // ── Restore persisted session ───────────────────────────
  useEffect(() => {
    const saved = localStorage.getItem(ACTIVE_SESSION_KEY);
    if (!saved) return;

    try {
      const savedSession = JSON.parse(saved);
      if (!savedSession.startTime) return;

      const now = Date.now();
      const start = new Date(savedSession.startTime).getTime();
      const elapsedSeconds = Math.floor((now - start) / 1000);

      // Restore selectedJobId; fall back gracefully
      setSelectedJobId(savedSession.selectedJobId || "none");
      setStartTime(new Date(savedSession.startTime));
      setTotalBreak(savedSession.totalBreak || 0);

      if (savedSession.trackingState === TS.BREAK) {
        setTrackingState(TS.BREAK);
        const breakStart = new Date(savedSession.breakStartTime).getTime();
        const breakSeconds = Math.floor((now - breakStart) / 1000);
        setBreakElapsed(breakSeconds);
        startBreakTick();
      } else {
        setTrackingState(TS.ACTIVE);
        setElapsed(elapsedSeconds);
        startWorkTick();
      }
    } catch (err) {
      console.error("Failed restoring session:", err);
      localStorage.removeItem(ACTIVE_SESSION_KEY);
    }
  }, [startBreakTick, startWorkTick]);

  useEffect(() => {
    return () => {
      stopWorkTick();
      stopBreakTick();
    };
  }, [stopWorkTick, stopBreakTick]);

  // ── Dropdown change handler ─────────────────────────────
  const handleJobSelectChange = (e) => {
    const val = e.target.value;
    if (val === "__create_new__") {
      setShowNewProjectModal(true);
    } else {
      setSelectedJobId(val);
    }
  };

  // ── Save new project ────────────────────────────────────
  const handleSaveNewProject = (newProject) => {
    setProjects((prev) => [...prev, newProject]);
    setSelectedJobId(newProject.id);
    setShowNewProjectModal(false);
  };

  // ── Start / Stop / Break ────────────────────────────────
  const handleStartTime = async () => {
    if (trackingState === TS.ACTIVE || trackingState === TS.STARTING) return;
    // ── Guest Mode ─────────────────────────────
    if (currentUser?.isGuest) {
      const now = new Date();

      setStartTime(now);
      setElapsed(0);
      setTotalBreak(0);
      setBreakElapsed(0);
      setTrackingState(TS.ACTIVE);

      startWorkTick();

      localStorage.setItem(
        ACTIVE_SESSION_KEY,
        JSON.stringify({
          trackingState: TS.ACTIVE,
          selectedJobId,
          startTime: now.toISOString(),
          totalBreak: 0,
          breakStartTime: null,
          session_id: "guest-session",
        }),
      );

      console.log("GUEST SESSION STARTED");

      return;
    }

    const webhookBlock = assertIntakeWebhookAllowed(currentUser);
    if (webhookBlock) {
      alert(webhookBlock);
      return;
    }

    try {
      setTrackingState(TS.STARTING);

      const now = new Date();

      const response = await postIntakeWebhook(currentUser, {
        raw_input: `clock me in on job ${selectedJobLabel}`,
        intent: "start_session",
        request_source: "cockpit_button",
        source: "cockpit-app",
        supabase_user_id: session?.user?.id || null,
        project_id: selectedJobId,
        project_name: selectedJobLabel,
        timestamp: now.toISOString(),
      });

      const result = await response.json();
      console.log("START SESSION RESULT:", result);
      if (!response.ok || result.success === false) {
        console.error("SESSION START REJECTED:", result);

        setTrackingState(TS.IDLE);

        alert(result.reason || "Unable to start session.");

        return;
      }
      setStartTime(now);
      setElapsed(0);
      setTotalBreak(0);
      setBreakElapsed(0);
      setTrackingState(TS.ACTIVE);
      startWorkTick();

      localStorage.setItem(
        ACTIVE_SESSION_KEY,
        JSON.stringify({
          trackingState: TS.ACTIVE,
          selectedJobId,
          startTime: now.toISOString(),
          totalBreak: 0,
          breakStartTime: null,
          session_id: result.session_id,
        }),
      );
    } catch (err) {
      console.error("START SESSION ERROR:", err);
      stopWorkTick();
      setTrackingState(TS.IDLE);
      localStorage.removeItem(ACTIVE_SESSION_KEY);
      alert("Active session may already exist.");
    }
  };

  const handleStartBreak = useCallback(async () => {
    const breakStart = new Date();
    const savedSession = JSON.parse(
      localStorage.getItem(ACTIVE_SESSION_KEY) || "{}",
    );

    try {
      await postIntakeWebhook(currentUser, {
        raw_input: "start break",
        intent: "start_break",
        session_id: savedSession.session_id,
        supabase_user_id: session?.user?.id || "",
        project_id: selectedJobId,
        project_name: selectedJobLabel,
        timestamp: breakStart.toISOString(),
        request_source: "cockpit_start_break",
      });
    } catch (err) {
      console.error("START BREAK WEBHOOK ERROR:", err);
      alert(err.message || "Unable to start break.");
      return;
    }

    stopWorkTick();
    setBreakStartTime(breakStart);
    setBreakElapsed(0);
    setTrackingState(TS.BREAK);

    localStorage.setItem(
      ACTIVE_SESSION_KEY,
      JSON.stringify({
        ...savedSession,
        trackingState: TS.BREAK,
        selectedJobId,
        startTime: startTime?.toISOString(),
        totalBreak,
        breakStartTime: breakStart.toISOString(),
      }),
    );

    startBreakTick();
  }, [
    stopWorkTick,
    startBreakTick,
    selectedJobId,
    selectedJobLabel,
    startTime,
    totalBreak,
    session,
    currentUser,
  ]);

  const handleStopBreak = useCallback(async () => {
    const updatedTotalBreak = totalBreak + breakElapsed;
    const savedSession = JSON.parse(
      localStorage.getItem(ACTIVE_SESSION_KEY) || "{}",
    );

    try {
      await postIntakeWebhook(currentUser, {
        raw_input: "stop break",
        intent: "stop_break",
        session_id: savedSession.session_id,
        supabase_user_id: session?.user?.id || "",
        project_id: selectedJobId,
        project_name: selectedJobLabel,
        break_seconds: breakElapsed,
        total_break_seconds: updatedTotalBreak,
        timestamp: new Date().toISOString(),
        request_source: "cockpit_stop_break",
      });
    } catch (err) {
      console.error("STOP BREAK WEBHOOK ERROR:", err);
      alert(err.message || "Unable to stop break.");
      return;
    }

    stopBreakTick();
    setTotalBreak(updatedTotalBreak);
    setBreakElapsed(0);
    setBreakStartTime(null);
    setTrackingState(TS.ACTIVE);

    localStorage.setItem(
      ACTIVE_SESSION_KEY,
      JSON.stringify({
        ...savedSession,
        trackingState: TS.ACTIVE,
        selectedJobId,
        startTime: startTime?.toISOString(),
        totalBreak: updatedTotalBreak,
        breakStartTime: null,
      }),
    );

    startWorkTick();
  }, [
    stopBreakTick,
    startWorkTick,
    breakElapsed,
    selectedJobId,
    selectedJobLabel,
    startTime,
    totalBreak,
    session,
    currentUser,
  ]);

  const handleStopTime = async () => {
    if (trackingState === TS.IDLE) return;

    const finalTotalBreak =
      trackingState === TS.BREAK ? totalBreak + breakElapsed : totalBreak;

    const savedSession = JSON.parse(
      localStorage.getItem(ACTIVE_SESSION_KEY) || "{}",
    );

    stopWorkTick();
    stopBreakTick();

    setTotalBreak(finalTotalBreak);
    setTrackingState(TS.IDLE);
    // ── Guest Mode ─────────────────────────────
    if (currentUser?.isGuest) {
      const finalTotalBreak =
        trackingState === TS.BREAK ? totalBreak + breakElapsed : totalBreak;

      stopWorkTick();
      stopBreakTick();

      setTotalBreak(finalTotalBreak);
      setTrackingState(TS.IDLE);

      localStorage.setItem(
        PENDING_FINALIZATION_KEY,
        JSON.stringify({
          pending: true,
          selectedJobId,
          startTime: startTime?.toISOString(),
          elapsed,
          totalBreak: finalTotalBreak,
          breakElapsed,
          session_id: "guest-session",
        }),
      );

      setShowModal(true);

      console.log("GUEST SESSION STOPPED");

      return;
    }
    try {
      await postIntakeWebhook(currentUser, {
        raw_input: `clock me out`,
        intent: "stop_session",
        session_id: savedSession.session_id,
        supabase_user_id: session?.user?.id || "",
        project_id: selectedJobId || "",
        project_name: selectedJobLabel || "",
        start_time: startTime?.toISOString(),
        end_time: new Date().toISOString(),
        break_minutes: Math.floor(finalTotalBreak / 60),
        total_hours_raw: elapsed / 3600,
        net_hours: (elapsed - finalTotalBreak) / 3600,
        work_completed: "",
        work_category: "",
        notes: "",
        payroll_status: "unpaid",
        approval_status: "pending",
        source: "CrewPilot App",
        request_source: "cockpit_stop_time",
        created_at: new Date().toISOString(),
      });
    } catch (err) {
      console.error("STOP SESSION WEBHOOK ERROR:", err);
      alert(err.message || "Unable to stop session.");
      return;
    }

    localStorage.setItem(
      PENDING_FINALIZATION_KEY,
      JSON.stringify({
        pending: true,
        selectedJobId,
        startTime: startTime?.toISOString(),
        elapsed,
        totalBreak: finalTotalBreak,
        breakElapsed,
        session_id: savedSession.session_id,
      }),
    );

    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setWorkCompleted("");
    setWorkCategory(CATEGORY_OPTIONS[0]);
    setNotes("");
    setStartTime(null);
    setElapsed(0);
    setTotalBreak(0);
    setBreakElapsed(0);
    setSelectedJobId("none");
  };

  const handleModalSubmit = async () => {
    try {
      const savedSession = JSON.parse(
        localStorage.getItem(PENDING_FINALIZATION_KEY) || "{}",
      );
      // ── Guest Mode ─────────────────────────────
      if (currentUser?.isGuest) {
        const guestActivity = JSON.parse(
          localStorage.getItem("guest_recent_activity") || "[]",
        );

        const proj = projects.find(
          (p) => p.id === (savedSession.selectedJobId || selectedJobId),
        );

        guestActivity.unshift({
          employeeName: currentUser?.employee_name || "Guest User",
          dateIso: new Date().toISOString().split("T")[0],
          workCompleted: workCompleted || "Work Session",
          notes: notes || "",
          projectName: proj?.name || "No Project Selected",
          hrs: ((elapsed - totalBreak) / 3600).toFixed(1),
          startTime: startTime?.toISOString(),
          endTime: new Date().toISOString(),
        });

        localStorage.setItem(
          "guest_recent_activity",
          JSON.stringify(guestActivity),
        );

        handleModalClose();

        localStorage.removeItem(PENDING_FINALIZATION_KEY);
        localStorage.removeItem(ACTIVE_SESSION_KEY);

        console.log("GUEST ACTIVITY SAVED");

        return;
      }

      const finalElapsed = Number(savedSession.elapsed ?? elapsed);
      const finalBreak = Number(savedSession.totalBreak ?? totalBreak);
      const finalStartTime = savedSession.startTime
        ? new Date(savedSession.startTime)
        : startTime;

      // Resolve project details at submit time
      const proj = projects.find(
        (p) => p.id === (savedSession.selectedJobId || selectedJobId),
      );

      const response = await postFinalizeWebhook(currentUser, {
        session_id: savedSession.session_id,
        supabase_user_id: session?.user?.id || "",
        project_id: proj?.id || selectedJobId || "",
        project_name: proj?.name || selectedJobLabel || "",
        project_number: proj?.number || "",
        client_name: proj?.client || "",
        start_time: finalStartTime?.toISOString(),
        end_time: new Date().toISOString(),
        break_minutes: Math.floor(finalBreak / 60),
        total_hours_raw: finalElapsed / 3600,
        net_hours: (finalElapsed - finalBreak) / 3600,
        work_completed: workCompleted || "",
        work_category: workCategory || "",
        notes: notes || "",
        raw_input: "",
        source: "CrewPilot App",
        request_source: "cockpit_modal",
        payroll_status: "unpaid",
        approval_status: "pending",
        created_at: new Date().toISOString(),
      });

      const result = await response.json();
      console.log("FINALIZE SESSION RESULT:", result);

      handleModalClose();
      localStorage.removeItem(PENDING_FINALIZATION_KEY);
      localStorage.removeItem(ACTIVE_SESSION_KEY);
    } catch (err) {
      console.error("FINALIZE SESSION ERROR:", err);
      alert("Unable to finalize session.");
    }
  };
const handleTouchStart = (e) => {
  touchStartX.current = e.touches[0].clientX;
};

const handleTouchEnd = (e) => {
  if (touchStartX.current === null) return;

  const deltaX =
    e.changedTouches[0].clientX - touchStartX.current;

  if (Math.abs(deltaX) > 80) {
    hardwareBackLog("CockpitHub touch-swipe onBack (bypasses hardware policy)", {
      deltaX,
      target: "mode_select via App.js onBack prop",
    });
    onBack?.();
  }

  touchStartX.current = null;
};
  const isTracking = trackingState === TS.ACTIVE;
  const isOnBreak = trackingState === TS.BREAK;
  const isIdle = trackingState === TS.IDLE;

  const statusLabel = isTracking
    ? "Tracking Active"
    : isOnBreak
      ? "On Break"
      : "Not Tracking";

  const statusColor = isTracking
    ? "#4ee2a1"
    : isOnBreak
      ? "#d4af37"
      : "rgba(255,255,255,0.45)";

  if (isDesktop) {
    return (
      <CommandCenterDesktop
        showRecords={canCreateClient || Boolean(currentUser?.isGuest)}
        onOpenCreateClient={onOpenCreateClient}
        onOpenCreateProject={onOpenCreateProject}
        onOpenTimeLogs={
          currentUser?.isGuest
            ? () => alert("Time Log Management available in full version.")
            : onOpenManualEntry
        }
        onOpenTimeTracking={onOpenTimeTrackingV2}
        onSignOut={onSignOut}
      />
    );
  }

  return (
  <div
    className="cockpit-hub-screen"
    onTouchStart={handleTouchStart}
    onTouchEnd={handleTouchEnd}
  >
      {/* TOPBAR */}
      <div className="cockpit-topbar">
        {desktopNav ? (null) : (
        <button className="cockpit-back-btn" onClick={onBack}>
          ← Back
        </button>
        )}

        <SignOutButton onClick={onSignOut} />
      </div>

      {/* HEADER */}
      <div className="cockpit-header">
        <div className="cockpit-brand-block">
          <h1 className="cockpit-title">Time & Session</h1>
          <p className="cockpit-subtitle">Time &amp; Session Control</p>
        </div>
      </div>

      {/* REQUEST PANEL — hidden during beta (COCKPIT_HUB_HIDE_BETA_CARDS) */}
      {!COCKPIT_HUB_HIDE_BETA_CARDS && (
        <button
          className="request-panel"
          onClick={() =>
            alert(
              "Voice requests are coming soon. For this demo, use Start Time → Stop Time → Submit Session.",
            )
          }
        >
          <div className="request-panel-left">
            <div className="request-mic-shell">
              <div className="request-mic-ring" />
              <div className="request-mic-core">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="9" y="2" width="6" height="12" rx="3" />
                  <path d="M5 10a7 7 0 0 0 14 0" />
                  <line x1="12" y1="19" x2="12" y2="22" />
                  <line x1="9" y1="22" x2="15" y2="22" />
                </svg>
              </div>
            </div>
          </div>
          <div className="request-panel-content">
            <h2>Voice Requests</h2>

            <p>
              Coming soon — speak tasks, notes, project updates and time entries.
            </p>

            <div className="request-panel-button">Preview Feature</div>
          </div>
        </button>
      )}

    {/* Legacy Time Tracking Panel Disabled */}
      {/* QUICK ACTIONS */}
      <section className="cockpit-section">
        <div className="section-heading">Quick Actions</div>
        <div className="quick-grid">
          {(canCreateClient || currentUser?.isGuest) && (
            <>
              <button
                className="quick-card quick-card-green"
                onClick={onOpenCreateClient}
              >
                <div className="quick-card-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </div>
                <h3>Create Client</h3>
                <p>Add and manage clients in the system.</p>
                <div className="quick-card-button">Create Client</div>
              </button>

              <button
                className="quick-card quick-card-purple"
                onClick={onOpenCreateProject}
              >
                <div className="quick-card-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3 7h18" />
                    <path d="M5 7V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v2" />
                    <rect x="3" y="7" width="18" height="13" rx="2" />
                  </svg>
                </div>
                <h3>Projects</h3>
                <p>Browse, create, and manage client projects.</p>
                <div className="quick-card-button">Projects</div>
              </button>
            </>
          )}
          <button
            className="quick-card quick-card-blue"
            onClick={
              currentUser?.isGuest
                ? () => alert("Time Log Management available in full version.")
                : onOpenManualEntry
            }
          >
            <div className="quick-card-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <line x1="10" y1="9" x2="8" y2="9" />
              </svg>
            </div>

            <h3>Time Logs</h3>

            <p>Search, review, and manage employee time logs.</p>

            <div className="quick-card-button">Time Logs</div>
          </button>

          {!COCKPIT_HUB_HIDE_BETA_CARDS && (
            <button
              className="quick-card quick-card-gold"
              onClick={onOpenTimeEntry}
            >
              <div className="quick-card-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </div>
              <h3>Orchestration</h3>
              <p>Scheduling, tasks and intelligent workflow coordination.</p>
              <div className="quick-card-button">Coming Soon</div>
            </button>
          )}
          <button
            className="quick-card quick-card-green"
            onClick={onOpenTimeTrackingV2}
          >
            <div className="quick-card-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="13" r="8" />
                <path d="M12 9v4l3 2" />
                <path d="M9 2h6" />
              </svg>
            </div>

            <h3>Time Tracking</h3>

            <p>Start, stop, and submit field time sessions.</p>

            <div className="quick-card-button">Launch</div>
          </button>
        </div>
      </section>

      {/* ── NEW PROJECT MODAL ─────────────────────────────── */}
      {showNewProjectModal && (
        <NewProjectModal
          onSave={handleSaveNewProject}
          onClose={() => setShowNewProjectModal(false)}
        />
      )}

      {/* ── SESSION COMPLETION MODAL ──────────────────────── */}
      {showModal && (
        <div
          className="tt-modal-overlay"
          onClick={(e) => e.target === e.currentTarget && handleModalClose()}
        >
          <div className="tt-modal">
            {/* Modal header */}
            <div className="tt-modal-header">
              <div className="tt-modal-header-left">
                <div className="tt-modal-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#d4af37"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                </div>
                <div>
                  <h2 className="tt-modal-title">Session Complete</h2>
                  <p className="tt-modal-subtitle">
                    {selectedJobLabel !== "None Selected"
                      ? selectedJobLabel
                      : "No job selected"}
                  </p>
                </div>
              </div>
              <button
                className="tt-modal-close"
                onClick={handleModalClose}
                aria-label="Close"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Summary stats */}
            <div className="tt-stats-row">
              <div className="tt-stat">
                <div className="tt-stat-value">{formatTime(elapsed)}</div>
                <div className="tt-stat-label">Total Time</div>
              </div>
              <div className="tt-stat-divider" />
              <div className="tt-stat">
                <div className="tt-stat-value" style={{ color: "#d4af37" }}>
                  {formatTime(totalBreak)}
                </div>
                <div className="tt-stat-label">Break Time</div>
              </div>
              <div className="tt-stat-divider" />
              <div className="tt-stat">
                <div className="tt-stat-value" style={{ color: "#4ee2a1" }}>
                  {formatTime(Math.max(0, elapsed - totalBreak))}
                </div>
                <div className="tt-stat-label">Net Work</div>
              </div>
            </div>

            <div className="tt-modal-start-row">
              <span className="tt-modal-start-label">Started:</span>
              <span className="tt-modal-start-val">
                {formatClock(startTime)}
              </span>
            </div>

            <CPInput
              className="cp-input-group--compact"
              label="Work Completed"
              placeholder="Brief description of work done…"
              value={workCompleted}
              onChange={setWorkCompleted}
            />

            <CPDropdown
              label="Category"
              value={workCategory}
              onChange={setWorkCategory}
              includePlaceholder={false}
              options={CATEGORY_OPTIONS.map((c) => ({
                value: c,
                label: c,
              }))}
            />

            <CPTextarea
              className="cp-input-group--compact"
              label="Notes"
              placeholder="Additional notes, materials used, issues encountered…"
              value={notes}
              onChange={setNotes}
              rows={3}
            />

            {/* Voice note placeholder */}
            <div className="tt-voice-placeholder">
              <div className="tt-voice-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="9" y="2" width="6" height="12" rx="3" />
                  <path d="M5 10a7 7 0 0 0 14 0" />
                  <line x1="12" y1="19" x2="12" y2="22" />
                  <line x1="9" y1="22" x2="15" y2="22" />
                </svg>
              </div>
              <div className="tt-voice-text">
                <div className="tt-voice-label">Voice Note</div>
                <div className="tt-voice-sub">
                  Coming soon — record a voice summary
                </div>
              </div>
              <div className="tt-voice-badge">Soon</div>
            </div>

            {/* Modal actions */}
            <div className="tt-modal-actions">
              <button
                className="gold-action-btn tt-submit-btn"
                onClick={handleModalSubmit}
              >
                Submit Session
              </button>
              <button
                className="outline-action-btn tt-discard-btn"
                onClick={handleModalClose}
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
